import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { TREE_ROOT_NODE_ID, TreeNode, TreeNodeApi, TreeNodeFile } from '@/entities/Tree';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import api from '@/utils/api';

import { MetaStore } from '../MetaStore';
import { OpenStore } from '../OpenStore';

export type TreeStoreParams = {
  level: number;
  data: TreeNode;
  normalizeData: (data: TreeNodeApi) => TreeNode | null;
  endpoint: string;
  getOrganizationId: () => number;
  parent?: TreeStore;
  isVirtualFolder?: boolean;
  virtualChildren?: TreeNode[];
  onAfterFetch?: (nodes: TreeNode[], store: TreeStore) => void;
};

type PrivateFields = '_isVirtualFolder';

export class TreeStore implements ILocalStore {
  private _endpoint: string;
  private _openStore = new OpenStore();
  private _normalizeData: TreeStoreParams['normalizeData'];
  private _getOrganizationId: () => number;
  private _parent?: TreeStore;
  private _onAfterFetch?: TreeStoreParams['onAfterFetch'];
  readonly level: number;

  private _isVirtualFolder = false;

  meta = new MetaStore();
  openMeta = new MetaStore();

  data: TreeNode;

  children: TreeStore[] = [];

  constructor({
    data,
    normalizeData,
    endpoint,
    level,
    getOrganizationId,
    isVirtualFolder,
    virtualChildren,
    parent,
    onAfterFetch,
  }: TreeStoreParams) {
    makeObservable<TreeStore, PrivateFields>(this, {
      data: observable.ref,
      children: observable.ref,
      _isVirtualFolder: observable,

      isOpen: computed,
      isVirtualFolder: computed,

      open: action.bound,
      toggle: action.bound,
      fetchTree: action.bound,
      addNode: action.bound,
      updateNode: action.bound,
      removeNode: action.bound,
      destroy: action.bound,
      reset: action.bound,
    });

    this.data = data;
    this.level = level;
    this._endpoint = endpoint;
    this._normalizeData = normalizeData;
    this._getOrganizationId = getOrganizationId;
    this._parent = parent;
    this._isVirtualFolder = Boolean(isVirtualFolder);
    this._onAfterFetch = onAfterFetch;

    if (this._isVirtualFolder) {
      const list = virtualChildren ?? [];

      this.children = list.map(
        (model) =>
          new TreeStore({
            level: this.level + 1,
            data: model,
            normalizeData: this._normalizeData,
            endpoint: this._endpoint,
            getOrganizationId: this._getOrganizationId,
            parent: this,
            onAfterFetch: this._onAfterFetch,
          }),
      );
    }
  }

  get parent(): TreeStore | undefined {
    return this._parent;
  }

  get isOpen(): boolean {
    return this._openStore.isOpen;
  }

  get isVirtualFolder() {
    return this._isVirtualFolder;
  }

  private _createChildStore(node: TreeNode, parent: TreeStore): TreeStore {
    return new TreeStore({
      level: parent.level + 1,
      data: node,
      normalizeData: this._normalizeData,
      endpoint: this._endpoint,
      getOrganizationId: this._getOrganizationId,
      parent,
      onAfterFetch: this._onAfterFetch,
    });
  }

  private _findStore(id: string): TreeStore | null {
    if (this.data.id === id) {
      return this;
    }

    for (const child of this.children) {
      const target = child._findStore(id);

      if (target) {
        return target;
      }
    }

    return null;
  }

  private _findParentStore(childId: string): TreeStore | null {
    for (const child of this.children) {
      if (child.data.id === childId) {
        return this;
      }

      const parent = child._findParentStore(childId);

      if (parent) {
        return parent;
      }
    }

    return null;
  }

  addNode(node: TreeNode, parentId: string | null = null) {
    const parentStore = parentId && parentId !== TREE_ROOT_NODE_ID ? (this._findStore(parentId) ?? this) : this;

    if (!parentStore || parentStore.data.type !== 'folder') {
      return;
    }

    const newChild = this._createChildStore(node, parentStore);

    parentStore.children = [...parentStore.children, newChild];

    parentStore.data = {
      ...parentStore.data,
      hasChildren: parentStore.children.length > 0,
    } as TreeNode;
  }

  updateNode(node: TreeNode) {
    const targetStore = this._findStore(node.id);

    if (!targetStore) {
      return;
    }

    targetStore.data = {
      ...targetStore.data,
      ...node,
    };
  }

  removeNode(nodeId: string) {
    const parentStore = this._findParentStore(nodeId);

    if (!parentStore) {
      return;
    }

    parentStore.children = parentStore.children.filter((child) => child.data.id !== nodeId);

    if (parentStore.data.type === 'folder') {
      parentStore.data = {
        ...parentStore.data,
        hasChildren: parentStore.children.length > 0,
      } as TreeNode;
    }
  }

  async open(): Promise<void> {
    if (!this.data.hasChildren) {
      return;
    }

    if (!this.meta.isLoaded && !this._isVirtualFolder) {
      await this.fetchTree();
    }

    this._openStore.open();
  }

  toggle(): void {
    if (this._openStore.isOpen) {
      this._openStore.close();
    } else {
      this.open();
    }
  }

  async fetchTree(): Promise<void> {
    if (this.meta.isLoading) {
      return;
    }

    if (this._isVirtualFolder) {
      return;
    }

    this.meta.setLoadedStartMeta();

    const parentId = this.data?.id !== TREE_ROOT_NODE_ID ? this.data.id : null;

    const response = await api<{ items: TreeNodeApi[] }>({
      endpoint: this._endpoint,
      data: {
        deep: false,
        parent_id: parentId,
      },
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    this._applyFetchedTreeItemRaws(response.data.items);
  }

  private _applyFetchedTreeItemRaws(itemRaws: TreeNodeApi[]): void {
    const groups = itemRaws.reduce(
      (acc, raw) => {
        const model = this._normalizeData(raw);

        if (!model) {
          return acc;
        }

        if (model.type !== 'folder' && model.groupType) {
          (acc.byGroup[model.groupType] ??= []).push(model);
        } else {
          acc.noGroup.push(model);
        }

        return acc;
      },
      { noGroup: [] as TreeNode[], byGroup: {} as Record<string, TreeNodeFile[]> },
    );

    runInAction(() => {
      const resultChildren: TreeStore[] = [];

      groups.noGroup.forEach((node) => {
        resultChildren.push(
          new TreeStore({
            level: this.level + 1,
            data: node,
            normalizeData: this._normalizeData,
            endpoint: this._endpoint,
            getOrganizationId: this._getOrganizationId,
            parent: this,
            onAfterFetch: this._onAfterFetch,
          }),
        );
      });

      Object.entries(groups.byGroup).forEach(([key, groupFiles]) => {
        if (groupFiles.length === 1) {
          resultChildren.push(
            new TreeStore({
              level: this.level + 1,
              data: groupFiles[0],
              normalizeData: this._normalizeData,
              endpoint: this._endpoint,
              getOrganizationId: this._getOrganizationId,
              parent: this,
              onAfterFetch: this._onAfterFetch,
            }),
          );

          return;
        }

        const virtualFolder: TreeNode = {
          id: `${this.data.id}-${key}`,
          name: key,
          type: 'folder',
          hasChildren: true,
          isPersonal: false,
          contentAvailable: true,
          availabilityBlockReason: null,
        };

        resultChildren.push(
          new TreeStore({
            level: this.level + 1,
            data: virtualFolder,
            normalizeData: this._normalizeData,
            endpoint: this._endpoint,
            getOrganizationId: this._getOrganizationId,
            isVirtualFolder: true,
            virtualChildren: groupFiles,
            parent: this,
            onAfterFetch: this._onAfterFetch,
          }),
        );
      });

      this.children = resultChildren;
      this.meta.setLoadedSuccessMeta();
    });

    const fetchedNodes: TreeNode[] = [...groups.noGroup, ...Object.values(groups.byGroup).flat()];

    if (this._onAfterFetch && fetchedNodes.length) {
      this._onAfterFetch(fetchedNodes, this);
    }
  }

  destroy() {
    this.meta.destroy();
    this.openMeta.destroy();
    this._openStore.destroy();
    this.children.forEach((store) => store.destroy());
  }

  reset(data?: TreeNode) {
    this.children.forEach((store) => store.destroy());
    this.children = [];
    this.meta.reset();
    this.openMeta.reset();
    this._openStore.close();

    if (data) {
      this.data = data;
    }
  }
}
